import { CRAYO_ENTRY, money, crayoPlanTerms } from "@/lib/compare/crayo";
import type { CompareContent } from "@/lib/compare/types";

const hobby = CRAYO_ENTRY;
const PRICING = "https://www.kapwing.com/pricing";

export const KAPWING_CONTENT: CompareContent = {
  slug: "crayo-vs-kapwing",
  competitorSlug: "kapwing",
  title: "Crayo vs Kapwing",
  seoTitle: "Crayo vs Kapwing: Pricing, Credits and Clipping Compared",
  seoDescription:
    "Crayo vs Kapwing with dated prices. Kapwing: general editor, $24 per member, watermarked free tier. Crayo: short-form clipper at one credit per finished clip.",
  cardVerdict: "Better than Crayo for free tier, 4K and team seats, behind on moment ranking and per-clip pricing.",
  bestFor: "Clippers deciding between a general editor and a clipper",
  datePublished: "2026-10-09",
  dateModified: "2026-10-25",
  verdict:
    `As of October 25, 2026, Crayo is the better pick if your output is short-form clips from podcasts and streams and you prefer paying one credit per finished clip on a ${money(hobby.monthly)} plan, with ranked moments, a timeline editor and a public API included. Kapwing is the better pick if you need a free tier, 4K export, team seats or dubbing. The evidence is below.`,
  pickCrayoIf: [
    "You make short-form clips and want them ranked: AutoClip and Find Viral Moments order moments by virality ranking, and Kapwing's Repurpose Studio publishes no score.",
    `You want one credit to equal one finished clip: Crayo Hobby gives ${hobby.credits} credits a month, and Kapwing does not publish how many credits one Repurpose clip costs.`,
    "You want the timeline editor and a public API on the cheapest plan: every Crayo plan opens each clip in an editor with a camera track, zooms and 32 caption styles and ships 34 API endpoints, while Kapwing has no public API.",
  ],
  pickThemIf: [
    "You need a free tier: Kapwing Free gives 10 credits a month at 720p with a watermark on every export. Crayo has no free plan.",
    "You export 4K or work in a team: Kapwing Pro exports 4K and is priced per member. Crayo tops out at 1080p and has no team seats.",
    "You dub videos, edit memes, or start from templates: Kapwing includes 50 minutes of dubbing a month on Pro and a large template library. Crayo has neither.",
  ],
  wins: [
    {
      claim: "Crayo ranks moments; Kapwing's Repurpose Studio does not score them.",
      evidence:
        "Kapwing's Repurpose page describes prompt-steered clips from uploads up to 3 hours, with no virality score. Crayo's AutoClip ranks moments and shows the planner's reasons, and Find Viral Moments returns up to 50 ranked moments to review before cutting. Crayo also steers the finder with a 500-character brief, the same way Kapwing's prompt steers Repurpose.",
      href: "https://www.kapwing.com/tools/repurpose",
    },
    {
      claim: "Crayo meters by finished clip; Kapwing meters by credits per AI task and does not publish the cost of a clip.",
      evidence:
        "Kapwing's pricing page, checked Oct 8, 2026, lists 1,000 credits a month on Pro and 10 on Free but no credit cost per Repurpose clip. Crayo charges 1 workflow credit per finished clip, and the free Clip Cutter costs nothing for manual ranges, so a month's bill is predictable before you upload.",
      href: PRICING,
    },
    {
      claim: "Crayo's paid plans never watermark; Kapwing watermarks every free export at 720p.",
      evidence:
        "Kapwing's watermark policy page says the watermark applies to all free projects, and the pricing page caps Free at 720p, 4-minute exports and 30 export minutes a month. Crayo's cheapest plan exports 1080p with no watermark. For most clippers a watermarked 720p file is a test, not a deliverable, so the real comparison is Kapwing Pro against Crayo Hobby.",
      href: "https://www.kapwing.com/help/our-watermark-policy/",
    },
    {
      claim: "Crayo has a public API with 34 endpoints and webhooks; Kapwing has no public API.",
      evidence:
        "Kapwing publishes no API or webhooks as of Oct 8, 2026. Crayo's API covers autoclip, exports, uploads, transcripts, voiceover, images and webhooks with a test endpoint, on every plan with docs at crayo.ai/developers.",
      href: "https://crayo.ai/developers",
    },
  ],
  concessions: [
    {
      claim: "Kapwing has a real free tier; Crayo has none.",
      evidence:
        "Kapwing Free, checked Oct 8, 2026, gives 10 credits and 10 minutes of subtitling a month, 720p exports with a watermark, and files kept for 3 days. Crayo's only free products are twelve browser tools that need no account. Kapwing is the right place to try a workflow before paying anything, which Crayo cannot offer.",
      href: PRICING,
    },
    {
      claim: "Kapwing exports 4K on Pro; Crayo stops at 1080p.",
      evidence:
        "Kapwing's pricing page lists 4K export and 120-minute exports on Pro at $24 per member a month. Crayo exports up to 1080p on every plan. Kapwing Free also exports, but at 720p with a watermark.",
      href: PRICING,
    },
    {
      claim: "Kapwing is built for teams; Crayo has no seats.",
      evidence:
        "Pro and Business are priced per member, Business adds 800 GB of storage and voice clones, and Enterprise adds SAML SSO. Crayo is one account per plan with no shared workspace, and its API issues one key per account, so a team on Crayo would share credentials rather than seats.",
      href: PRICING,
    },
    {
      claim: "Kapwing dubs into 40+ languages and captions 100+; Crayo transcribes 56 and does not dub.",
      evidence:
        "Kapwing Pro includes 50 minutes of dubbing a month and lists 100+ caption languages, with a template and meme library Crayo does not try to match. Crayo's spoken-language picker has 56 entries.",
      href: PRICING,
    },
  ],
  typicalMonth: {
    scenario: "One 60-minute source a week, eight clips each: 240 source minutes and 32 clips a month.",
    crayo: {
      plan: "Hobby",
      cost: `${money(hobby.monthly)}/mo or about ${money(hobby.yearlyPerMonth)}/mo yearly`,
      usage: `32 of ${hobby.credits} workflow credits and about 32 of ${hobby.exportMinutes} export minutes`,
      note: "Prices from the live plan table. One seat covers it.",
    },
    them: {
      plan: "Pro",
      cost: "$24/member/mo or $16/member/mo yearly ($192/yr)",
      usage:
        "Credits per clip not published. Each 60-minute source fits the 3-hour Repurpose limit, 240 minutes fit the 1,000 subtitle minutes, and 32 short clips fit the 120-minute export cap. Free's 4-minute, 30-minute-a-month export caps rule it out for this volume.",
      note: "Per-member pricing, per the Kapwing pricing page checked Oct 8, 2026.",
    },
  },
  test: {
    status: "scheduled",
    source: "A 45-minute two-person podcast recording owned by Crayo",
    plans: "Kapwing Pro and Crayo Clipper, same file, same day, defaults first then one tuned run each",
  },
  billingNote:
    "Kapwing bills per member, so each seat is its own charge, and the pricing page says refunds are not issued once you upgrade. There is no trial for paid plans, and the pricing page does not state whether unused credits roll over.",
  faqs: [
    {
      question: "Is Crayo cheaper than Kapwing for clipping?",
      answer:
        `It depends on the term. Crayo Hobby is ${crayoPlanTerms(hobby)} for ${hobby.credits} credits, and one credit is one finished clip. Kapwing Pro is $24 per member a month, or $16 per member on the annual plan ($192 a year), for 1,000 credits, so Kapwing Pro is lower on monthly and yearly billing and Crayo's quarterly plan is lower than Kapwing's monthly price. Kapwing does not publish how many credits one Repurpose clip costs. Kapwing Free is $0, so if a watermark, 720p and 4-minute exports are acceptable, Kapwing is cheaper than any Crayo plan. A second editor on Kapwing doubles the bill; on Crayo a second person shares one account.`,
    },
    {
      question: "Can Kapwing turn podcasts and streams into short clips like Crayo?",
      answer:
        "Yes. Kapwing's Repurpose Studio takes uploads up to 3 hours, lets you steer the clips with a prompt, and uses Auto Speaker Focus to keep the active speaker centered. It does not rank or score the moments it finds. Crayo's AutoClip takes a YouTube or TikTok link or a file up to 3 hours and 4 GB, returns 1 to 20 ranked clips of 15 to 120 seconds, and applies a face-aware camera with four layouts. Both reframe for 9:16; only Crayo publishes a virality ranking. Kapwing Free caps uploads at 2 GB and Pro at 6 GB; Crayo accepts 4 GB on every plan.",
    },
    {
      question: "Does Crayo have a free plan like Kapwing?",
      answer:
        `No. Crayo has no free plan and no trial. The only free products are twelve browser tools at crayo.ai/features/free that need no account and never upload the file. The entry plan is Hobby at ${money(hobby.monthly)} a month. Kapwing Free gives 10 credits and 10 minutes of subtitling a month, 720p exports with a watermark on every one, a 4-minute export limit, 30 export minutes a month, and files that are kept for 3 days. Kapwing's watermark policy page says the watermark applies to all free projects.`,
    },
    {
      question: "How does Kapwing's credit system work compared to Crayo's?",
      answer:
        "Kapwing gives each member 10 credits a month on Free, 1,000 on Pro and 4,000 on Business, spent per AI task, and the pricing page does not publish a per-clip cost. A third-party cost tracker, costbench.com, reports that AI features stop working once credits run out; Crayo has not verified this against Kapwing's own help center. Crayo gives a monthly pool of workflow credits, one per finished clip, with no rollover and separate top-up packs. Find Viral Moments lists ranked moments before any credit is spent, and the free Clip Cutter and the timeline editor cost no credits.",
    },
    {
      question: "Does Kapwing give refunds?",
      answer:
        "Kapwing's pricing page, checked Oct 8, 2026, says refunds are not issued once you upgrade, and paid plans have no free trial. Crayo's policy is the same on refunds: all sales are final. The difference is in cancelling. Crayo cancels in one click from Settings, the plan stays active to the end of the cycle, and nothing further is charged. Kapwing's pricing page does not describe how to cancel. One dated review is linked in the billing section of this page. Neither side offers a trial of a paid plan, so read both pricing pages before paying.",
    },
    {
      question: "Does Crayo export 4K like Kapwing?",
      answer:
        "No. Crayo exports up to 1080p on every plan, with no watermark on any paid plan. Kapwing exports 720p with a watermark on Free and 4K on Pro at $24 per member a month. If 4K delivery is a requirement, Kapwing wins that row. For vertical clips posted to TikTok, Reels and Shorts, 1080p is the resolution those platforms serve. Kapwing Free caps each export at 4 minutes and 30 minutes a month, so 4K there is a paid-only feature.",
    },
    {
      question: "Does Crayo have team seats like Kapwing?",
      answer:
        "No. Crayo is one account per plan with no shared workspace, and the public API uses one key per account. Kapwing prices Pro and Business per member, Business adds 800 GB of storage, voice clones and lip sync, and Enterprise adds SAML SSO. Teams that edit the same projects together should pick Kapwing for that reason alone. Solo creators pay for one member on either side; the per-member price only matters once a second editor joins.",
    },
    {
      question: "Kapwing vs CapCut vs Crayo: which should a clipper pick?",
      answer:
        "Kapwing and CapCut are general editors; Crayo is a clipping editor built for turning long-form video into shorts. Kapwing publishes its own CapCut comparison posts under kapwing.com/resources, so read those for the editor-to-editor view, and see the Crayo vs CapCut page for CapCut's terms. Pick Crayo when the job is finding and cutting ranked clips from podcasts, streams and interviews, opening them in a timeline editor, and paying one credit per finished clip, with a public API. Pick Kapwing when the job is general editing, memes, templates, dubbing or 4K in a team.",
    },
  ],
  changelog: [
    {
      date: "2026-10-09",
      text: "First published. Kapwing prices checked Oct 8, 2026: Free $0 with watermark at 720p, Pro $24 per member monthly or $16 per member annual ($192/yr), Business $64 per member or $50 annual ($600/yr), no refunds once upgraded.",
    },
    {
      date: "2026-10-25",
      text: `Crayo prices updated to the October 25, 2026 ladder: Hobby is ${crayoPlanTerms(hobby)}, with quarterly the default term at checkout. Verdicts and the corrections box re-checked at the new prices; competitor prices unchanged from the October 8 to 9 check.`,
    },
  ],
  relatedSpokes: ["crayo-vs-capcut", "crayo-vs-veed", "crayo-vs-clipchamp"],
  featurePath: "/features/auto-clip",
  ctaHref: "https://crayo.ai/create/auto-clip",
  ctaLabel: "Clip a video now",
  targetQueries: [
    "kapwing alternative",
    "kapwing vs capcut",
    "kapwing pricing",
    "kapwing vs crayo",
    "kapwing alternative free",
    "crayo vs kapwing",
  ],
};
